import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaUsers, FaRunning, FaPlus, FaEdit } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';
import { enqueueAndPoll } from '../utils/jobPolling';

const SceneCreator = ({ 
  storyId, 
  sceneNumber, 
  previousImageUrl, 
  onSceneGenerated, 
  existingCharacters = [] 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Scene configuration
  const [sceneSetting, setSceneSetting] = useState('');
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [sceneAction, setSceneAction] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  
  // Character management
  const [availableCharacters, setAvailableCharacters] = useState(existingCharacters);
  const [showCharacterManager, setShowCharacterManager] = useState(false);
  const [newCharacterForm, setNewCharacterForm] = useState({
    name: '',
    type: 'human',
    age: '',
    gender: '',
    description: ''
  });

  // Load available characters
  useEffect(() => {
    if (storyId) {
      loadCharacters();
    }
  }, [storyId]);

  const loadCharacters = async () => {
    if (!storyId) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/story/${storyId}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableCharacters(data.characters || []);
      }
    } catch (error) {
      console.error('Error loading characters:', error);
    }
  };

  // Handle character selection
  const handleCharacterSelect = (characterId, isSelected) => {
    if (isSelected) {
      const character = availableCharacters.find(c => c.characterId === characterId);
      if (character) {
        setSelectedCharacters(prev => [...prev, character]);
      }
    } else {
      setSelectedCharacters(prev => prev.filter(c => c.characterId !== characterId));
    }
  };

  // Create new character quickly
  const handleCreateQuickCharacter = async (e) => {
    e.preventDefault();
    if (!newCharacterForm.name.trim()) {
      setError('Character name is required');
      return;
    }

    setLoading(true);
    try {
      const characterData = {
        name: newCharacterForm.name.trim(),
        type: newCharacterForm.type,
        age: newCharacterForm.age || null,
        gender: newCharacterForm.gender || null,
        description: newCharacterForm.description.trim()
      };

      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storyId,
          characterData
        })
      });

      if (!response.ok) throw new Error('Failed to create character');

      const data = await response.json();
      setAvailableCharacters(prev => [...prev, data.character]);
      setSelectedCharacters(prev => [...prev, data.character]);
      
      // Reset form
      setNewCharacterForm({
        name: '',
        type: 'human',
        age: '',
        gender: '',
        description: ''
      });
      setError(null);
    } catch (error) {
      console.error('Error creating character:', error);
      setError('Failed to create character');
    } finally {
      setLoading(false);
    }
  };

  // Generate scene
  const handleGenerateScene = async () => {
    if (!sceneSetting.trim() || selectedCharacters.length === 0 || !sceneAction.trim()) {
      setError('Please fill in all required fields: Scene setting, select at least one character, and describe the action.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build the scene prompt
      const characterDescriptions = selectedCharacters.map(char => {
        let desc = char.name;
        if (char.age) desc += ` (${char.age})`;
        if (char.description) desc += ` - ${char.description}`;
        return desc;
      }).join(', ');

      const scenePrompt = `Scene ${sceneNumber}: ${sceneSetting}

Characters: ${characterDescriptions}

Action: ${sceneAction}

${additionalDetails ? `Additional Details: ${additionalDetails}` : ''}

Please generate a kid-friendly scene description showing this scene with the specified characters performing the described action.`;

      // For first scene, generate scene description instead of artwork
      if (sceneNumber === 1) {
        const requestBody = {
          imageUrl: previousImageUrl,
          title: `Scene ${sceneNumber}`,
          artistName: 'Young Artist',
          sceneNumber: sceneNumber,
          prompt: scenePrompt,
          userId: 'anonymous'
        };

        const response = await fetch(`${API_ENDPOINTS.backendBase}/api/story/generate-scene`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate scene description');
        }

        const data = await response.json();
        
        // Create characters in character manager
        await createCharactersInManager(selectedCharacters);
        
        if (onSceneGenerated) {
          onSceneGenerated({
            title: `Scene ${sceneNumber}`,
            description: data.scene?.description || scenePrompt,
            imageUrl: previousImageUrl, // Use original artwork for first scene
            characters: selectedCharacters,
            setting: sceneSetting,
            action: sceneAction,
            additionalDetails: additionalDetails
          });
        }
      } else {
        // For subsequent scenes, generate artwork as before
        const requestBody = {
          previousImageUrl: previousImageUrl,
          scenePrompt: scenePrompt,
          storyId: storyId,
          characterIds: selectedCharacters.map(char => char.characterId || char.id) // Pass character IDs for image combination
        };

        // Use background worker with polling
        const data = await enqueueAndPoll(
          API_ENDPOINTS.generateStoryImageFirebase || API_ENDPOINTS.generateStoryImage,
          requestBody,
          {
            interval: 2000, // Poll every 2 seconds
            maxAttempts: 150, // 5 minutes max
            onProgress: (status, job, attempts) => {
              // Update loading state if available
              if (setLoading) {
                setLoading(true);
        }
            },
            baseUrl: 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api'
          }
        );
        
        if (onSceneGenerated) {
          onSceneGenerated({
            title: `Scene ${sceneNumber}`,
            description: scenePrompt,
            imageUrl: data.generatedImageUrl || data.imageUrl,
            characters: selectedCharacters,
            setting: sceneSetting,
            action: sceneAction,
            additionalDetails: additionalDetails
          });
        }
      }

    } catch (error) {
      console.error('Error generating scene:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Create characters in character manager
  const createCharactersInManager = async (characters) => {
    try {
      for (const character of characters) {
        const characterData = {
          name: character.name,
          type: character.type || 'human',
          age: character.age || null,
          gender: character.gender || null,
          description: character.description || '',
          role: `Character from Scene ${sceneNumber}`
        };

        const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storyId,
            characterData
          })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Created character in manager:', data.character);
        }
      }
    } catch (error) {
      console.error('Error creating characters in manager:', error);
    }
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm border-2 border-amber-200 rounded-2xl shadow-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <FaMapMarkerAlt className="text-amber-600" />
        <h3 className="text-xl font-semibold text-amber-800">
          Create Scene {sceneNumber}
        </h3>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Scene Setting */}
        <div>
          <label className="block text-sm font-semibold text-amber-800 mb-2">
            🌍 Scene Setting *
          </label>
          <input
            type="text"
            value={sceneSetting}
            onChange={(e) => setSceneSetting(e.target.value)}
            placeholder="Where is this scene taking place? (e.g., in a magical forest, at school, in a castle)"
            className="w-full px-4 py-3 border-2 border-amber-100 rounded-lg focus:border-amber-400 focus:outline-none bg-amber-50/50 text-amber-900"
          />
        </div>

        {/* Character Selection */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-semibold text-amber-800">
              👥 Characters in this Scene *
            </label>
            <button
              onClick={() => setShowCharacterManager(!showCharacterManager)}
              className="text-amber-600 hover:text-amber-800 text-sm font-medium"
            >
              {showCharacterManager ? 'Hide' : 'Manage Characters'}
            </button>
          </div>

          {/* Quick Character Creation */}
          {showCharacterManager && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <h5 className="font-medium text-amber-800 mb-3">Quick Add Character</h5>
              <form onSubmit={handleCreateQuickCharacter} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={newCharacterForm.name}
                  onChange={(e) => setNewCharacterForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Character name"
                  className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                  required
                />
                <select
                  value={newCharacterForm.type}
                  onChange={(e) => setNewCharacterForm(prev => ({ ...prev, type: e.target.value }))}
                  className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                >
                  <option value="human">Human</option>
                  <option value="animal">Animal</option>
                  <option value="object">Object</option>
                </select>
                <input
                  type="text"
                  value={newCharacterForm.age}
                  onChange={(e) => setNewCharacterForm(prev => ({ ...prev, age: e.target.value }))}
                  placeholder="Age (optional)"
                  className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                />
                <select
                  value={newCharacterForm.gender}
                  onChange={(e) => setNewCharacterForm(prev => ({ ...prev, gender: e.target.value }))}
                  className="px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                >
                  <option value="">Gender (optional)</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                </select>
                <div className="md:col-span-2">
                  <input
                    type="text"
                    value={newCharacterForm.description}
                    onChange={(e) => setNewCharacterForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description (optional)"
                    className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:border-amber-400"
                  />
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-gradient-to-r from-amber-500 to-amber-400 text-white px-4 py-2 rounded-lg hover:shadow-md transition-all duration-300 disabled:opacity-50"
                  >
                    {loading ? 'Adding...' : 'Add Character'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Character Selection List */}
          <div className="space-y-2">
            {availableCharacters.length === 0 ? (
              <p className="text-amber-600 text-sm">No characters created yet. Add some characters first!</p>
            ) : (
              availableCharacters.map((character) => {
                const isSelected = selectedCharacters.some(c => c.characterId === character.characterId);
                return (
                  <label
                    key={character.characterId}
                    className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-amber-400 bg-amber-100' 
                        : 'border-amber-200 bg-amber-50/50 hover:border-amber-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleCharacterSelect(character.characterId, e.target.checked)}
                      className="text-amber-600 focus:ring-amber-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-amber-800">{character.name}</div>
                      <div className="text-sm text-amber-600 capitalize">{character.type}</div>
                      {character.description && (
                        <div className="text-sm text-amber-700">{character.description}</div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>

          {selectedCharacters.length > 0 && (
            <div className="mt-3 p-3 bg-amber-100 rounded-lg">
              <p className="text-sm font-medium text-amber-800">
                Selected: {selectedCharacters.map(c => c.name).join(', ')}
              </p>
            </div>
          )}
        </div>

        {/* Scene Action */}
        <div>
          <label className="block text-sm font-semibold text-amber-800 mb-2">
            🏃‍♂️ What's happening? *
          </label>
          <textarea
            value={sceneAction}
            onChange={(e) => setSceneAction(e.target.value)}
            placeholder="Describe what the characters are doing in this scene (e.g., playing in the garden, having a tea party, exploring a cave)"
            className="w-full px-4 py-3 border-2 border-amber-100 rounded-lg focus:border-amber-400 focus:outline-none bg-amber-50/50 text-amber-900"
            rows="3"
          />
        </div>

        {/* Additional Details */}
        <div>
          <label className="block text-sm font-semibold text-amber-800 mb-2">
            ✨ Additional Details (optional)
          </label>
          <textarea
            value={additionalDetails}
            onChange={(e) => setAdditionalDetails(e.target.value)}
            placeholder="Any other details you'd like to include? (e.g., weather, time of day, special objects, mood)"
            className="w-full px-4 py-3 border-2 border-amber-100 rounded-lg focus:border-amber-400 focus:outline-none bg-amber-50/50 text-amber-900"
            rows="2"
          />
        </div>

        {/* Generate Button */}
        <div className="flex justify-center">
          <button
            onClick={handleGenerateScene}
            disabled={loading || !sceneSetting.trim() || selectedCharacters.length === 0 || !sceneAction.trim()}
            className="bg-gradient-to-r from-amber-500 to-amber-400 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {sceneNumber === 1 ? 'Creating Scene Description...' : 'Creating Scene...'}
              </>
            ) : (
              <>
                <FaPlus />
                {sceneNumber === 1 ? 'Generate Scene Description' : 'Generate Scene Artwork'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SceneCreator; 